import React, { useEffect, useState, useRef } from 'react'
import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser'
import { Button } from '@components/inputs/Button'
import Box from '@components/base/Box'
import Modal from '@components/base/Modal'

const QRScanner = ({ onRead }: { onRead: (text: string) => void }) => {
  const [isScanning, setIsScanning] = useState(false)
  const [scanError, setScanError] = useState('')
  const videoRef = useRef<HTMLVideoElement>(null)
  const onReadRef = useRef(onRead)

  useEffect(() => {
    onReadRef.current = onRead
  }, [onRead])

  useEffect(() => {
    if (!isScanning) return

    const codeReader = new BrowserQRCodeReader()
    let stopped = false
    let scannerControls: IScannerControls | undefined

    const startScanning = async () => {
      const devices = await BrowserQRCodeReader.listVideoInputDevices()
      if (stopped) return
      const selectedDeviceId = devices?.[1]?.deviceId ?? devices?.[0]?.deviceId

      if (videoRef.current) {
        scannerControls = await codeReader.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current,
          (result, _error, controls) => {
            if (stopped) {
              controls.stop()
              return
            }
            if (result) {
              stopped = true
              controls.stop()
              setIsScanning(false)
              onReadRef.current(result.getText())
            }
          }
        )
        if (stopped) scannerControls.stop()
      }
    }
    startScanning().catch(() => {
      if (stopped) return
      stopped = true
      scannerControls?.stop()
      setIsScanning(false)
      setScanError(
        'Unable to start camera. Check camera permissions and try again.'
      )
    })

    return () => {
      stopped = true
      scannerControls?.stop()
    }
  }, [isScanning])

  return (
    <div>
      {!isScanning && (
        <Box>
          <Button
            title="Scan Ticket"
            size="auto"
            onClick={() => {
              setScanError('')
              setIsScanning(true)
            }}
            className="ml-1 whitespace-nowrap"
          />
        </Box>
      )}
      {scanError && <p role="alert">{scanError}</p>}
      <Modal
        show={isScanning}
        title="Attendee QR Code Verification"
        setShow={setIsScanning}
      >
        <Box className="w-full relative">
          <Box className="text-xs text-center mb-6">
            Please scan the attendee`s QR code from their registration
            confirmation.
          </Box>
          <video ref={videoRef}></video>
        </Box>
      </Modal>
    </div>
  )
}

export default QRScanner
